import type { GameType } from '@ottd/api';

import { activeLocale, intlLocale, monthNames, tr } from '@/lib/locale';

/**
 * Small display helpers. Most money/goal/duration/date/relative strings arrive
 * pre-formatted from the API (localised there by `Accept-Language`); these
 * cover the few raw numbers the SPA renders itself (points, populations,
 * balances, counts).
 *
 * Every helper takes the locale as a trailing optional argument defaulting to
 * the active UI language, so tests can pin it without touching i18next.
 */

/** Thousands-separated integer, e.g. `34495` → `"34,495"` (en) / `"34 495"` (pl). */
export function formatNumber(value: number | null | undefined, locale?: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toLocaleString(intlLocale(locale ?? activeLocale()));
}

/** Signed points, e.g. `32` → `"+32"`, `-5` → `"-5"`, `0` → `"+0"`. */
export function formatSignedPoints(value: number, locale?: string): string {
  return value >= 0 ? `+${formatNumber(value, locale)}` : formatNumber(value, locale);
}

/** Euro balance, e.g. `29480` → `"29,480€"`. Handles negatives as `"-…€"`. */
export function formatMoney(value: number | null | undefined, locale?: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const abs = Math.abs(value).toLocaleString(intlLocale(locale ?? activeLocale()));
  return value < 0 ? `-${abs}€` : `${abs}€`;
}

/**
 * Population, deliberately unseparated: `4013`, not `4,013`. Populations read
 * as counts rather than money, so the separator only adds noise - and being
 * locale-independent keeps it parseable wherever a goal figure is read back.
 */
export function formatPopulation(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return String(Math.trunc(value));
}

/**
 * What a company is measured by in the race to the goal: town population for
 * City Builder (`"398 pop"`), company value for Company Value (`"18,000,000€"`).
 */
export function formatGoalFigure(figure: number, type: GameType | null, locale?: string): string {
  return type === 'CV'
    ? formatMoney(figure, locale)
    : `${formatPopulation(figure)} ${tr('common:units.population')}`;
}

/**
 * An in-game month key → a readable date, e.g. `"2030-08"` → `"Aug 2030"`.
 * Null in, null out - samples predating monthly bucketing carry no month.
 */
export function formatInGameMonth(month: string | null | undefined): string | null {
  if (!month) return null;
  const [year, index] = month.split('-');
  const name = monthNames()[Number(index) - 1];

  return name === undefined ? null : `${name} ${year}`;
}

/** Percentage 0–100 clamped and rounded, e.g. `62.4` → `62`. */
export function clampPercent(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

const SECOND_MS = 1000;
const MINUTE_S = 60;
const HOUR_S = 3600;
const DAY_S = 86400;
/** Under this many seconds we say "just now" rather than "0 minutes ago". */
const JUST_NOW_S = 45;

/**
 * Coarse "10 minutes ago" for an ISO timestamp; `now` is injectable for tests.
 * Em dash when null/invalid, "just now" under 45s, then minutes / hours / days.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now = Date.now(),
  locale?: string,
): string {
  if (!iso) return '-';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '-';
  const seconds = Math.max(0, Math.round((now - then) / SECOND_MS));
  if (seconds < JUST_NOW_S) return tr('common:time.justNow');
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale ?? activeLocale()), {
    numeric: 'always',
  });
  if (seconds < HOUR_S) return rtf.format(-Math.round(seconds / MINUTE_S), 'minute');
  if (seconds < DAY_S) return rtf.format(-Math.round(seconds / HOUR_S), 'hour');
  return rtf.format(-Math.round(seconds / DAY_S), 'day');
}
